import {
  SystemPrompt,
  CreateSystemPromptPayload,
  UpdateSystemPromptPayload,
  PreviewSystemPromptPayload,
  PreviewSystemPromptResponse,
} from '@/types/api';
import { getApiBaseUrl, handleResponse } from './client';

// System Prompt API calls
export async function getSystemPrompts(): Promise<SystemPrompt[]> {
  const response = await fetch(`${getApiBaseUrl()}/system-prompts`);
  return handleResponse<SystemPrompt[]>(response);
}

export async function getSystemPromptRaw(id: string): Promise<SystemPrompt> {
  const response = await fetch(`${getApiBaseUrl()}/system-prompts/${id}/raw`);
  return handleResponse<SystemPrompt>(response);
}

export async function createSystemPrompt(
  payload: CreateSystemPromptPayload,
): Promise<SystemPrompt> {
  const response = await fetch(`${getApiBaseUrl()}/system-prompts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleResponse<SystemPrompt>(response);
}

export async function updateSystemPrompt(
  id: string,
  payload: UpdateSystemPromptPayload,
): Promise<SystemPrompt> {
  const response = await fetch(`${getApiBaseUrl()}/system-prompts/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleResponse<SystemPrompt>(response);
}

export async function setDefaultSystemPrompt(
  id: string,
): Promise<SystemPrompt> {
  const response = await fetch(
    `${getApiBaseUrl()}/system-prompts/${id}/set-default`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    },
  );
  return handleResponse<SystemPrompt>(response);
}

export async function deleteSystemPrompt(id: string): Promise<void> {
  const response = await fetch(`${getApiBaseUrl()}/system-prompts/${id}`, {
    method: 'DELETE',
  });
  await handleResponse<void>(response);
}

export async function duplicateSystemPrompt(id: string): Promise<SystemPrompt> {
  const response = await fetch(`${getApiBaseUrl()}/system-prompts/${id}/duplicate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  return handleResponse<SystemPrompt>(response);
}

export async function previewSystemPrompt(
  payload: PreviewSystemPromptPayload,
): Promise<PreviewSystemPromptResponse> {
  const response = await fetch(`${getApiBaseUrl()}/system-prompts/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleResponse<PreviewSystemPromptResponse>(response);
}

export async function getSystemPromptTools(): Promise<string[]> {
  const response = await fetch(`${getApiBaseUrl()}/system-prompts/tools`);
  return handleResponse<string[]>(response);
}

// Tool management API calls
export async function getAllToolNames(): Promise<string[]> {
  const response = await fetch(`${getApiBaseUrl()}/system-prompts/all-tools`);
  return handleResponse<{ tools: string[] }>(response).then(
    (data) => data.tools,
  );
}

export async function getSystemPromptEnabledTools(
  id: string,
): Promise<string[] | 'all'> {
  const response = await fetch(
    `${getApiBaseUrl()}/system-prompts/${id}/enabled-tools`,
  );
  return handleResponse<{ enabledTools: string[] | 'all' }>(response).then(
    (data) => data.enabledTools,
  );
}

export async function getSystemPromptToolsForPrompt(
  id: string,
): Promise<string[]> {
  const response = await fetch(
    `${getApiBaseUrl()}/system-prompts/${id}/tools-for-prompt`,
  );
  return handleResponse<{ tools: string[] }>(response).then(
    (data) => data.tools,
  );
}

export async function setSystemPromptEnabledTools(
  id: string,
  enabledTools: string[] | 'all',
): Promise<string[] | 'all'> {
  const body =
    enabledTools === 'all'
      ? { enabledTools: 'all' }
      : { enabledToolsArray: enabledTools };

  const response = await fetch(
    `${getApiBaseUrl()}/system-prompts/${id}/enabled-tools`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  return handleResponse<{ enabledTools: string[] | 'all' }>(response).then(
    (data) => data.enabledTools,
  );
}

export async function toggleSystemPromptTool(
  id: string,
  toolName: string,
): Promise<string[] | 'all'> {
  const response = await fetch(
    `${getApiBaseUrl()}/system-prompts/${id}/toggle-tool`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ toolName }),
    },
  );
  return handleResponse<{ enabledTools: string[] | 'all' }>(response).then(
    (data) => data.enabledTools,
  );
}